import { useState, useEffect } from 'react'

export type ProjectCategory =
  | 'Oficinas corporativas'
  | 'Sucursales bancarias'
  | 'Retail y tiendas'
  | 'Educación / institucional'
  | 'Industrial / especial'
  | 'Otros'

export interface ProjectData {
  id?: number
  name: string
  category: ProjectCategory
  image: string
  description: string
  year: number
  m2: number
  comuna: string
  featured: boolean
}

const STORAGE_KEY = 'forax_projects'

// Proyectos iniciales (se cargan solo la primera vez)
const INITIAL_PROJECTS: ProjectData[] = [
  // OFICINAS
  {
    id: 1,
    name: 'Oficinas Corporativas Providencia',
    category: 'Oficinas corporativas',
    image:
      'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1000',
    description: 'Habilitación de 2.500 m² de espacios colaborativos',
    year: 2024,
    m2: 2500,
    comuna: 'Providencia',
    featured: true,
  },
  {
    id: 2,
    name: 'Torre Titanium',
    category: 'Oficinas corporativas',
    image:
      'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&q=80&w=1000',
    description: 'Pisos ejecutivos con diseño premium y tecnología integrada',
    year: 2023,
    m2: 1200,
    comuna: 'Las Condes',
    featured: true,
  },
  {
    id: 3,
    name: 'Edificio Apoquindo',
    category: 'Oficinas corporativas',
    image:
      'https://images.unsplash.com/photo-1572025442641-415cd4a2569c?auto=format&fit=crop&q=80&w=1000',
    description: 'Remodelación integral de 3 pisos corporativos',
    year: 2023,
    m2: 1800,
    comuna: 'Las Condes',
    featured: true,
  },
  {
    id: 4,
    name: 'Campus Corporativo Las Condes',
    category: 'Oficinas corporativas',
    image:
      'https://images.unsplash.com/photo-1600607686527-6fb886090705?auto=format&fit=crop&q=80&w=1000',
    description: 'Espacios de trabajo flexible y áreas de descanso',
    year: 2024,
    m2: 3500,
    comuna: 'Las Condes',
    featured: false,
  },
  // BANCOS
  {
    id: 5,
    name: 'Sucursal BCI Mall Plaza',
    category: 'Sucursales bancarias',
    image:
      'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1000',
    description: 'Diseño moderno y funcional enfocado en la experiencia',
    year: 2024,
    m2: 450,
    comuna: 'La Florida',
    featured: false,
  },
  {
    id: 6,
    name: 'Banco de Chile Vitacura',
    category: 'Sucursales bancarias',
    image:
      'https://images.unsplash.com/photo-1601597111158-2fceff292cdc?auto=format&fit=crop&q=80&w=1000',
    description: 'Renovación completa de sucursal con nuevos estándares',
    year: 2023,
    m2: 380,
    comuna: 'Vitacura',
    featured: false,
  },
  {
    id: 7,
    name: 'Scotiabank Costanera Center',
    category: 'Sucursales bancarias',
    image:
      'https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?auto=format&fit=crop&q=80&w=1000',
    description: 'Habilitación de oficina premium para banca privada',
    year: 2024,
    m2: 600,
    comuna: 'Providencia',
    featured: false,
  },
  {
    id: 8,
    name: 'Itaú Las Condes',
    category: 'Sucursales bancarias',
    image:
      'https://images.unsplash.com/photo-1565514020176-dbf2277cc168?auto=format&fit=crop&q=80&w=1000',
    description: 'Sucursal digital con tecnología de punta',
    year: 2023,
    m2: 320,
    comuna: 'Las Condes',
    featured: false,
  },
  // RETAIL
  {
    id: 9,
    name: 'Skechers Parque Arauco',
    category: 'Retail y tiendas',
    image:
      'https://images.unsplash.com/photo-1567449303078-57a67168a66a?auto=format&fit=crop&q=80&w=1000',
    description: 'Local comercial flagship de 180 m²',
    year: 2024,
    m2: 180,
    comuna: 'Las Condes',
    featured: false,
  },
  {
    id: 10,
    name: 'Mattel Chile Showroom',
    category: 'Retail y tiendas',
    image:
      'https://images.unsplash.com/photo-1582653291997-079a1c04e5a1?auto=format&fit=crop&q=80&w=1000',
    description: 'Espacio de exhibición interactivo para clientes B2B',
    year: 2023,
    m2: 450,
    comuna: 'Huechuraba',
    featured: false,
  },
  {
    id: 11,
    name: 'Strip Center La Reina',
    category: 'Retail y tiendas',
    image:
      'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=1000',
    description: 'Habilitación simultánea de 5 locales comerciales',
    year: 2024,
    m2: 850,
    comuna: 'La Reina',
    featured: false,
  },
  {
    id: 12,
    name: 'Lápiz López Costanera',
    category: 'Retail y tiendas',
    image:
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1000',
    description: 'Tienda flagship de 250 m² con nuevo concepto',
    year: 2023,
    m2: 250,
    comuna: 'Providencia',
    featured: false,
  },
]

export function useProjects() {
  const [projects, setProjects] = useState<ProjectData[]>(() => {
    // Inicializar desde localStorage o usar proyectos iniciales
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch {
        return INITIAL_PROJECTS
      }
    }
    return INITIAL_PROJECTS
  })

  // Sincronizar con localStorage cuando cambien los proyectos
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects))
  }, [projects])

  const addProject = (project: ProjectData) => {
    const newId = Math.max(...projects.map((p) => p.id || 0), 0) + 1
    setProjects([{ ...project, id: newId }, ...projects])
  }

  const updateProject = (id: number, updatedProject: ProjectData) => {
    setProjects(
      projects.map((p) => (p.id === id ? { ...updatedProject, id } : p)),
    )
  }

  const deleteProject = (id: number) => {
    setProjects(projects.filter((p) => p.id !== id))
  }

  const toggleFeatured = (id: number): boolean => {
    const project = projects.find((p) => p.id === id)
    if (!project) return false

    // Si ya está destacado, lo quitamos (siempre permitido)
    if (project.featured) {
      updateProject(id, { ...project, featured: false })
      return true
    }

    // Si no está destacado, verificamos si hay cupo (máximo 3)
    const featuredCount = projects.filter((p) => p.featured).length
    if (featuredCount >= 3) {
      return false
    }

    updateProject(id, { ...project, featured: true })
    return true
  }

  return {
    projects,
    addProject,
    updateProject,
    deleteProject,
    toggleFeatured,
  }
}
